import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    MenuItem,
    Button,
    Box,
    Paper,
} from '@mui/material';
import RestartAltOutlined from '@mui/icons-material/RestartAltOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import {
    searchWinnersReport,
    fetchWinnersReportPage,
    clearWinnersReport,
} from '../../../store/actions/winnersReportActions';
import { convertToCSV, downloadCSV } from '../../../services/downloadUtils';

const TABLE_COLUMNS = [
    { key: 'transactionId', header: 'TRANSACTION ID' },
    { key: 'orderId', header: 'ORDER ID' },
    { key: 'gigyaId', header: 'USER GIGYA ID' },
    { key: 'packCode', header: 'PACK CODE' },
    { key: 'packItem', header: 'PACK ITEM' },
    { key: 'eventType', header: 'EVENT TYPE' },
    { key: 'promotionName', header: 'PROMOTION NAME' },
    { key: 'prizeType', header: 'PRIZE TYPE' },
    { key: 'prizeId', header: 'PRIZE ID' },
    { key: 'prizeName', header: 'PRIZE NAME' },
    { key: 'prizeValue', header: 'PRIZE VALUE' },
    { key: 'prizeQuantity', header: 'PRIZE QUANTITY' },
    { key: 'claimStatus', header: 'CLAIM STATUS' },
    { key: 'createdDateUtc', header: 'CREATE DATE UTC' },
    { key: 'updatedDateUtc', header: 'UPDATED DATE UTC' },
];

const PROMOTION_TYPES = [
    { value: 'GOLDEN_TICKET', label: 'Golden Ticket' },
    { value: 'LUCKY_PROMOTION', label: 'Lucky Promotion' },
    { value: 'REWARD', label: 'Reward' },
    { value: 'PICK_A_PRIZE', label: 'Pick A Prize' },
    { value: 'MGM', label: 'MGM' },
];

const WinnersReport = () => {
    const dispatch = useDispatch();
    const { reportData, page, rowsPerPage, hasSearched } = useSelector(
        (state) => state.winnersReport
    );

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [gigyaId, setGigyaId] = useState('');
    const [eventType, setEventType] = useState('');
    const [formError, setFormError] = useState('');

    const handleSearch = () => {
        setFormError('');
        if (!startDate || !endDate) {
            setFormError('Please select both start and end dates.');
            return;
        }
        if (dayjs(startDate).isAfter(dayjs(endDate))) {
            setFormError('Start date cannot be after end date.');
            return;
        }

        const fromDate = dayjs(startDate).startOf('day').toISOString();
        const toDate = dayjs(endDate).endOf('day').toISOString();

        dispatch(
            searchWinnersReport(fromDate, toDate, {
                gigyaId,
                eventType,
                limit: rowsPerPage,
            })
        );
    };

    const handleReset = () => {
        setStartDate(null);
        setEndDate(null);
        setGigyaId('');
        setEventType('');
        setFormError('');
        dispatch(clearWinnersReport());
    };

    const handlePageChange = (event, newPage) => {
        dispatch(fetchWinnersReportPage(newPage, rowsPerPage));
    };

    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        dispatch(fetchWinnersReportPage(0, newRowsPerPage));
    };

    const handleDownloadCSV = () => {
        if (reportData?.results?.length > 0) {
            const csvString = convertToCSV(reportData.results, TABLE_COLUMNS);
            const timestamp = dayjs().format('YYYYMMDDHHmmss');
            downloadCSV(csvString, 'Winners-Report-' + timestamp + '.csv');
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Filter Panel */}
            <Paper sx={{ p: 3, mb: 3, maxWidth: '50%' }}>
                <Box
                    sx={{
                        fontFamily: 'Lato, sans-serif',
                        fontWeight: 500,
                        fontSize: '13px',
                        color: '#364153',
                        mb: 2,
                    }}
                >
                    Search Transaction Criteria
                </Box>

                {/* Row 1: Date Pickers */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <DatePicker
                        label="From*"
                        value={startDate}
                        onChange={(date) => setStartDate(date)}
                        slotProps={{
                            textField: { size: 'small', fullWidth: true },
                        }}
                    />
                    <DatePicker
                        label="To*"
                        value={endDate}
                        onChange={(date) => setEndDate(date)}
                        slotProps={{
                            textField: { size: 'small', fullWidth: true },
                        }}
                    />
                </Box>

                {/* Row 2: Query By + Gigya ID */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 2,
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        select
                        label="Query By"
                        value="gigyaId"
                        size="small"
                        sx={{ minWidth: 150 }}
                        inputProps={{ readOnly: true }}
                    >
                        <MenuItem value="gigyaId">Gigya ID</MenuItem>
                    </TextField>
                    <TextField
                        value={gigyaId}
                        onChange={(e) => setGigyaId(e.target.value)}
                        size="small"
                        placeholder="Enter Gigya ID"
                        fullWidth
                    />
                </Box>

                {/* Row 3: Promotion Type */}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        select
                        label="Promotion Type"
                        value={eventType}
                        onChange={(e) => setEventType(e.target.value)}
                        size="small"
                        fullWidth
                    >
                        {PROMOTION_TYPES.map((pt) => (
                            <MenuItem key={pt.value} value={pt.value}>
                                {pt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                {/* Form Error */}
                {formError && (
                    <Box sx={{ color: 'error.main', fontSize: 12, mb: 2 }}>
                        {formError}
                    </Box>
                )}

                {/* Buttons Row */}
                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
                >
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<RestartAltOutlined />}
                        onClick={handleReset}
                    >
                        Reset query
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SearchOutlined />}
                        onClick={handleSearch}
                        sx={{
                            backgroundColor: '#078C2B',
                            '&:hover': { backgroundColor: '#066b22' },
                        }}
                    >
                        Search
                    </Button>
                </Box>
            </Paper>

            {/* Empty State */}
            {!hasSearched && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                    }}
                >
                    <Box
                        sx={{
                            width: 120,
                            height: 120,
                            backgroundColor: '#e6f9ed',
                            border: '2px solid #b3e6c7',
                            borderRadius: '50%',
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SearchOutlined
                            sx={{ fontSize: 48, color: '#078C2B' }}
                        />
                    </Box>
                    <Box
                        sx={{
                            fontSize: 18,
                            fontWeight: 600,
                            mb: 1,
                        }}
                    >
                        Search for Transaction Reports
                    </Box>
                    <Box
                        sx={{
                            fontSize: 14,
                            color: '#6b7280',
                            textAlign: 'center',
                            maxWidth: 400,
                        }}
                    >
                        Use the filters above to search and generate your
                        report. Configure your criteria and click
                        &apos;Search&apos; to get started.
                    </Box>
                </Box>
            )}

            {/* No Records */}
            {hasSearched && !reportData?.results?.length && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 4,
                        color: '#6b7280',
                    }}
                >
                    No records found
                </Box>
            )}

            {/* Report Table */}
            {hasSearched && reportData?.results?.length > 0 && (
                <Paper sx={{ mb: 2 }}>
                    {/* Download option currently disabled due to PII data exposure */}
                    <Box
                        sx={{
                            display: 'none',
                            justifyContent: 'flex-end',
                            p: 2,
                        }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadOutlined />}
                            onClick={handleDownloadCSV}
                        >
                            Download CSV
                        </Button>
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                    {TABLE_COLUMNS.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            sx={{
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                fontSize: 12,
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {col.header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.results.map((row, idx) => (
                                    <TableRow key={idx}>
                                        {TABLE_COLUMNS.map((col) => (
                                            <TableCell
                                                key={col.key}
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    fontSize: 12,
                                                }}
                                            >
                                                {row[col.key] ?? ''}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={reportData.totalCount || 0}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}
        </Box>
    );
};

export default WinnersReport;
